import { Component, OnInit, Input, Output, EventEmitter, ViewChildren, QueryList } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { AlertsService } from 'src/app/demo/service/alerts.service';
import { ChangeSingleSelectTwo, FieldTwo, MultiSelectFieldTwo, OptionTwo, Rules, TextareaFieldTwo, TextFieldTwo } from 'src/app/demo/interfaces/form-print.interface';
import { FileSelectEvent } from 'primeng/fileupload';
import { Tooltip } from 'primeng/tooltip';

@Component({
  selector: 'app-form-print-two',
  templateUrl: './form-print-two.component.html',
  styleUrl: './form-print-two.component.scss'
})
export class FormPrintTwoComponent implements OnInit {
  @ViewChildren('tooltipTextAreas', { read: Tooltip }) tooltipTextAreas!: QueryList<Tooltip>;
  @ViewChildren('tooltipMultiselect', { read: Tooltip }) tooltipMultiselect!: QueryList<Tooltip>;
  @ViewChildren('tooltipMultiSelectItems', { read: Tooltip }) tooltipMultiSelectItems!: QueryList<Tooltip>;
  @ViewChildren('tooltipSelect', { read: Tooltip }) tooltipSelect!: QueryList<Tooltip>;
  @ViewChildren('tooltipSelectItems', { read: Tooltip }) tooltipSelectItems!: QueryList<Tooltip>;
  @ViewChildren('tooltipinputSwitch', { read: Tooltip }) tooltipinputSwitch!: QueryList<Tooltip>;
  @ViewChildren('tooltipInputNumbers', { read: Tooltip }) tooltipInputNumbers!: QueryList<Tooltip>;
  @ViewChildren('tooltipInputTexts', { read: Tooltip }) tooltipInputTexts!: QueryList<Tooltip>;
  @ViewChildren('inputNumbersBlockPaste') inputNumbersBlockPaste!: QueryList<any>;

  @Input() textButtonSubmit: string = 'Guardar';
  @Input() isFilterFields: boolean = false;
  @Input() formFilter: boolean = false;
  @Input() hideAlertToSubmit: boolean = false;
  @Input() fieldsToCompare: { controlName: string, matchingControlName: string }[] = [];
  @Input() showButtonsSubmit: boolean = true;
  @Input() updatedValues: { key: string, value: any, disabled?: boolean, rules?: Rules | null }[] = [];
  @Input() showClearFilters: boolean = false;
  @Output() sendQuestions: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Output() cancelQuestions: EventEmitter<void> = new EventEmitter<void>();
  @Output() onChangeSingleSelect: EventEmitter<ChangeSingleSelectTwo> = new EventEmitter<ChangeSingleSelectTwo>();
  @Output() onChangeValueField: EventEmitter<{ key: string, value: string | number }> = new EventEmitter<{ key: string, value: string | number }>();
  @Output() onChangeValueDateField: EventEmitter<{ key: string, value: Date, selectedField: number }> = new EventEmitter<{ key: string, value: Date, selectedField: number }>();
  @Output() validFormPrint: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  _questions: FieldTwo[] = [];

  @Input()
  set questions(value: FieldTwo[]) {
    // Evita que los valores [], undefined, o null, cambie el los valores de los campos
    if (!value || value.length === 0) return;

    this._questions = value;

    // Si el formulario ya existe, solo actualizamos los campos
    if (this.form) {
      value.forEach((question) => {
        const control = this.form.get(question.key);

        if (control) {
          if (control.disabled !== question.disabled) {
            question.disabled ? control.disable() : control.enable();
          }

          const newValue = this.transformFieldValue(question.typeInput, question.value);

          if (control.value !== newValue) {
            control.setValue(newValue, { emitEvent: false });
          }

          const validators = this.generateValidators(question.rules);
          control.setValidators(validators);
          control.updateValueAndValidity({ emitEvent: false });
        } else {
          const validators = this.generateValidators(question.rules);
          const initialValue = this.transformFieldValue(question.typeInput, question.value);

          this.form.addControl(
            question.key,
            new FormControl({ value: initialValue, disabled: question.disabled }, validators)
          );
        }
      });

      this.emitValidationStatus();

      // Agrega el evento para los campos que tiene la propiedad blockPaste
      this.inputNumbersBlockPaste.forEach(inputNumber => {
        const nativeInput: HTMLInputElement = inputNumber?.input?.nativeElement;
        if (nativeInput) {
          nativeInput.addEventListener('paste', (event: ClipboardEvent) => this.preventPaste(event));
          nativeInput.addEventListener('copy', (event: ClipboardEvent) => this.preventPaste(event));
          nativeInput.addEventListener('cut', (event: ClipboardEvent) => this.preventPaste(event));
        }
      });

    } else {
      // Primera vez: se construye el formulario completo
      const objectForm = {};

      value.forEach((question) => {
        const validators = this.generateValidators(question.rules);
        const initialValue = this.transformFieldValue(question.typeInput, question.value);

        objectForm[question.key] = new FormControl(
          { value: initialValue, disabled: question.disabled },
          validators
        );
      });

      this.form = new FormGroup(objectForm);
    }
  }

  get questions(): FieldTwo[] {
    return this._questions;
  }

  transformFieldValue(typeInput: string, value: any): number | boolean | string | Date | null {
    let transformValue: number | boolean | string | Date | null;
    switch (typeInput) {
      case 'date':
        transformValue = this.normalizeDate(value);
        break;
      default:
        transformValue = value;
        break;
    }

    return transformValue
  }

  private normalizeDate(value: any): Date | null {
    if (!value) return null;
    return value instanceof Date ? value : new Date(value);
  }

  form: FormGroup = null;

  constructor(
    private alertService: AlertsService,
  ) { }

  ngOnInit(): void {
    this.emitValidationStatus();
    this.listenToFormChanges()
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * @description Método que genera un conjunto de validadores basados en las reglas proporcionadas.
    * @param {Record<string, any>} rules - Objeto JSON con las configuraciones de validación.
    * @returns {ValidatorFn[]} Arreglo de validadores para aplicar al campo del formulario.
    */
  generateValidators(rules: Record<string, any>): ValidatorFn[] {
    if (!rules) return [];
    const validators: ValidatorFn[] = [];
    for (const key in rules) {
      if (Object.prototype.hasOwnProperty.call(rules, key)) {
        switch (key) {
          case 'required':
            // `required` es un flag booleano (ver `email` más abajo): a diferencia de
            // minLength/maxLength/min/max, que siempre traen un valor a aplicar, aquí hay
            // que revisar el VALOR, no solo si la llave existe — `rules: { required: false }`
            // debe dejar el campo sin validador, no forzarlo igual que `required: true`
            // (mismo criterio que ya usa form-print.component.ts).
            if (rules[key] === true) validators.push(Validators.required);
            break;
          case 'minLength':
            validators.push(Validators.minLength(rules[key]));
            break;
          case 'maxLength':
            validators.push(Validators.maxLength(rules[key]));
            break;
          case 'email':
            // Mismo caso que `required`: es un flag booleano.
            if (rules[key] === true) validators.push(Validators.email);
            break;
          case 'min':
            validators.push(Validators.min(rules[key]));
            break;
          case 'max':
            validators.push(Validators.max(rules[key]));
            break;
        }
      }
    }
    return validators;
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * @description Metodo que envia las respuestas del formulario al componente padre.  
    */
  submitForm(): void {
    if (this.form.valid) {
      if (!this.formFilter) {
        if (!this.hideAlertToSubmit) {
          this.alertService.showConfirmAlert({ html: '¿Desea enviar la información registrada?' }).then(isConfirm => {
            if (isConfirm.isConfirmed) this.sendQuestions.emit(this.form);
          });
        } else {
          this.sendQuestions.emit(this.form);
        }
      } else {
        this.sendQuestions.emit(this.form);
      }
    } else this.form.markAllAsTouched();
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-12-06
    * @description Metodo que carga el archivo seleccionado al valor del formulario. 
    * @param $event Evento emitido por el campo file.
    * @param key Nombre del campo.
    */
  onSelectFileToByField($event: FileSelectEvent, key: string): void {
    const field = this.form.get(key);
    if (field) field.setValue($event.currentFiles[0]);
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-12-06
    * @description Metodo que emite el valor sobre los campos numericos. 
    * @param $event Evento emitido por el campo tipo numerico.
    */
  onChangeValueInput(value: any, key: string): void {
    this.onChangeValueField.emit({ key, value: value })
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-12-11
    * @description Metodo que limpia los campos del formulario y emite el submit del formulario. 
    */
  onClickClearFiltersForm(): void {
    this.form.reset();
    this.sendQuestions.emit(this.form);
  }

  /**
    * @author Ministerio de Cultura
    * @createdate 2025-04-01
    * @description Verifica si un campo del formulario es inválido.
    * El campo es considerado inválido si tiene errores de validación y ha sido modificado (dirty) o tocado (touched).
    *
    * @param {string} questionKey - Nombre del campo a validar dentro del formulario `compromisoForm`.
    * @returns {boolean} `true` si el campo es inválido, `false` en caso contrario.
    */
  isInvalid(questionKey: string): boolean {
    const control = this.form.get(questionKey);
    return control && control.invalid && (control.dirty || control.touched);
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2025-04-02
    * @description Se suscribe a los cambios en los campos del formulario y actualiza el estado de validación.
    * Cada vez que un usuario modifique un campo, se verificará si el formulario es válido o no.
    */
  private listenToFormChanges(): void {
    this.form.valueChanges.subscribe(() => {
      this.emitValidationStatus();
      this.forceResizeAll();
    });
  }


  /**
   * @author Ministerio de Cultura
   * @createdate 2025-06-06
   * @description Ajusta la altura de los campos textarea según la cantidad de texto contenido en cada uno.
   */
  forceResizeAll() {
    setTimeout(() => {
      this.tooltipTextAreas.forEach((textAreaRef: Tooltip) => {
        const nativeTextarea: HTMLTextAreaElement = textAreaRef.el.nativeElement;
        if (nativeTextarea) {
          nativeTextarea.style.height = 'auto';
          nativeTextarea.style.height = nativeTextarea.scrollHeight + 'px';
        }
      });
    }, 0);
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2025-04-02
    * @description Emite el estado de validez del formulario. 
    *              Si todos los campos están deshabilitados, emite `true`. 
    *              De lo contrario, emite el estado de validez del formulario.
    */
  private emitValidationStatus(): void {
    const isFormFullyDisabled = this._questions.every(item => item.disabled === true);
    this.validFormPrint.emit(isFormFullyDisabled ? true : this.form.valid);
  }

  handleInputNumber($value: any, question: any): void {

    if (!$value) return;

    const control = this.form.get(question.key);
    if (!control) return;

    // 1. Eliminar el prefijo "$" si lo hay
    let cleanInput = $value.replace(/\$\s?/g, '');

    // 1. Eliminar el prefijo "COP" si lo hay
    // let cleanInput = $value.replace(/COP\s?/gi, '')

    // 2. Eliminar puntos (separadores de miles)
    cleanInput = cleanInput.replace(/\./g, '');

    // 3. Reemplazar la coma decimal por punto decimal
    cleanInput = cleanInput.replace(',', '.');

    // 4. Convertir a número
    let parsedValue = Number(cleanInput);

    if (isNaN(parsedValue)) {
      control.setValue(null);
      return;
    }

    if (question.rules?.maxLength != null) {
      const value = String(parsedValue || '')
      const truncated = Number(value.slice(0, question.rules.maxLength));
      if (control.value !== truncated)
        control.setValue(truncated === 0 ? undefined : truncated);
    }

    if (question.rules?.max != null) {
      const value = Number(parsedValue)
      control.setValue(value > 100 ? question.rules.max : value);
    }

    this.onChangeValueInput(parsedValue, question.key);
  }

  handleOverlayClose(question: MultiSelectFieldTwo, index: number) {
    const values = this.form.get(question.key)?.value;

    const result = values.map(id => {
      const match = question.options.find(opt => opt.value === id);
      return match ? { value: match.value, keyvalue: match.keyValue } : null;
    }).filter(Boolean);

    this.onChangeSingleSelect.emit({ value: values, key: question.key, keyValue: result });
  }

  onMultiSelectChange(question: MultiSelectFieldTwo) {
    if (!question.maxSelection) return

    const control = this.form.get(question.key);
    if (!control) return;

    const selectedValues = control.value || [];

    if (selectedValues.length > question.maxSelection) {
      control.setValue(selectedValues.slice(0, question.maxSelection));
    }
  }

  isDisabledOption(item: any, question: MultiSelectFieldTwo): boolean {
    if (!question.maxSelection) return false

    const control = this.form.get(question.key) || null;
    if (!control) return false;

    const selectedValues = control.value || [];

    // si ya llegó al máximo y el item no está seleccionado, lo deshabilita
    return (
      Array.isArray(selectedValues) && selectedValues.length >= question.maxSelection && !selectedValues.includes(item.value)
    );
  }

  inputBlur(value: any, _key: string) {
    let text = value.trim();
    // text = text.replace(/"/g, '\'');

    if (text === text.toUpperCase() && text !== '' && this.countWords(text) > 1 && this.formFilter === false) {
      text = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

      this.alertService.showAlertNotification({
        title: 'Mayúsculas no permitidas',
        html: 'El contenido no debe estar escrito totalmente en mayúsculas.',
        icon: 'error',
        timer: 4000
      });
    }

    this.onChangeSingleSelect.emit({ value: String(text), key: _key });
  }

  countWords(texto: string): number {
    return texto
      .trim()
      .split(/\s+/)
      .filter(p => p !== '')
      .length;
  }

  isMaxLengthReached(question: FieldTwo): boolean {
    const value = String(this.form.get(question.key)?.value || '');
    return question.rules?.maxLength != null && value.length >= question.rules.maxLength;
  }

  getKeyValueById(id: number, options: OptionTwo[]): string | null {
    const result = options.find(item => item.value === id);
    return result ? result.keyValue : null;
  }

  /**
   * @author Ministerio de Cultura
   * @createdate 2025-07-08
   * @description Detecta el cambio de estado del campo tipo toggle (`p-inputSwitch`) al hacer clic sobre el componente.
   * Emite el nuevo valor (`true` o `false`) del interruptor después de un breve retardo para permitir la animación.
   *
   * @param $event Evento emitido por el componente, contiene la propiedad `checked` con el nuevo estado (`true` | `false`).
   * @param question Objeto que contiene la información del campo, incluyendo su clave única (`key`).
   */
  handleChangeInputSwitch($event, question: FieldTwo) {
    setTimeout(() => {
      this.onChangeSingleSelect.emit({ value: $event.checked, key: question.key })
    }, 400)
  }

  /**
   * @author Ministerio de Cultura
   * @createdate 2025-07-08
   * @description Cambia el estado del campo tipo toggle (`p-inputSwitch`) al presionar la tecla Enter durante la navegación por teclado.
   * Este método simula un clic sobre el interruptor y permite ejecutar la animación correctamente.
   *
   * @param _key Identificador único del campo toggle, utilizado para localizar el elemento en el DOM.
   */
  toggleSwitch(_key: string): void {
    const control = this.form.get(_key);
    const focusToggle = this.tooltipinputSwitch.find(
      (toggle) => toggle.el.nativeElement.id === _key
    );

    const toggleMain = focusToggle?.el.nativeElement.querySelector('.p-inputswitch') as HTMLElement;
    toggleMain?.click();

    // Espera breve para permitir la animación del componente antes de emitir el cambio.
    if (control) {
      setTimeout(() => {
        this.onChangeSingleSelect.emit({ value: control.value, key: _key });
      }, 400);
    }
  }

  /**
   * @author Ministerio de Cultura
   * @createdate 2025-07-08
   * @description Muestra u oculta el tooltip (mensaje de ayuda) cuando el campo recibe o pierde el foco,
   * al navegar mediante la tecla TAB. Aplica para campos de tipo `select`, `multiselect`, `toggle`, `number`, `text` y `textarea`.
   * 
   * Este método se activa mediante los eventos `(onFocus) o (focus)` y `(onBlur) o (blur)` del componente correspondiente,
   * y busca el elemento asociado en el DOM para ejecutar la acción deseada (`show` u `hide`).
   * 
   * @param typeField Tipo de componente (`simple_select`, `multiselect`, `toggle`, `number`, `text`, `textarea`)
   * @param _key Identificador único del campo, utilizado para localizar el elemento en el DOM
   * @param tooltip Texto del mensaje de ayuda. Si es `null`, no se realiza ninguna acción.
   * @param actionTooltip Acción a ejecutar sobre el tooltip: `'show'` para mostrarlo o `'hide'` para ocultarlo
   */
  handleTooltipFocus(
    typeField: 'simple_select' | 'multiselect' | 'toggle' | 'number' | 'text' | 'textarea',
    _key: string,
    tooltip: string | null,
    actionTooltip: 'show' | 'hide'
  ) {
    let focusedElement: Tooltip

    if (tooltip) {

      switch (typeField) {
        case 'simple_select':
          focusedElement = this.tooltipSelect.find((focus) => focus.el.nativeElement.children[0].id === _key);
          break;
        case 'multiselect':
          focusedElement = this.tooltipMultiselect.find((focus) => focus.el.nativeElement.children[0].id === _key);
          break;
        case 'toggle':
          focusedElement = this.tooltipinputSwitch.find((focus) => focus.el.nativeElement.id === _key);
          break;
        case 'number':
          focusedElement = this.tooltipInputNumbers.find((focus) => focus.el.nativeElement.children[0].children[0].id === _key);
          break;
        case 'text':
          focusedElement = this.tooltipInputTexts.find((focus) => focus.el.nativeElement.id === _key);
          break;
        case 'textarea':
          focusedElement = this.tooltipTextAreas.find((focus) => focus.el.nativeElement.id === _key);
          break;
      }

      const actions = {
        show: () => focusedElement.show(),
        hide: () => focusedElement.hide()
      };

      actions[actionTooltip]?.();
    }
  }

  /**
   * @author Ministerio de Cultura
   * @createdate 2025-07-08
   * @description Habilita el tooltip (mensaje de ayuda) al navegar por teclado 
   * usando las teclas de dirección (arriba y abajo) en campos tipo `select` o `multiselect`. 
   * Este comportamiento aplica únicamente cuando el panel desplegable del componente está abierto.
   * 
   * El método detecta el elemento activo (`aria-activedescendant`) y muestra el tooltip asociado,
   * ocultando los demás tooltips del mismo grupo.
   * 
   * @param typeField Tipo de componente (`simple_select` o `multiselect`)
   * @param key Identificador único del campo, utilizado para seleccionar los elementos del DOM correspondientes
   */
  handleTooltipFocusOnSelect(
    typeField: 'simple_select' | 'multiselect',
    key: string,
  ) {
    let focusedElement: Tooltip
    switch (typeField) {
      case 'simple_select':
        document.querySelector(`.p-dropdown-label[aria-controls^="${key}"]`).addEventListener('keyup', (e: KeyboardEvent) => {
          const target = e.target as HTMLElement;
          const idItem = target.getAttribute('aria-activedescendant');
          const el = document.querySelector(`#${idItem} div[tooltipposition]`);
          if (!el) return;

          this.tooltipSelectItems.forEach(tooltip => tooltip.hide());
          focusedElement = this.tooltipSelectItems.find((focus) => focus.el.nativeElement === el);
          focusedElement?.show()
        });
        break;
      case 'multiselect':
        document.querySelector(`.p-multiselect-filter[aria-owns^="${key}"]`).addEventListener('keyup', (e: KeyboardEvent) => {
          const target = e.target as HTMLElement;
          const idItem = target.getAttribute('aria-activedescendant');
          const el = document.querySelector(`#${idItem} div[tooltipposition]`);
          if (!el) return;

          this.tooltipMultiSelectItems.forEach(tooltip => tooltip.hide());
          focusedElement = this.tooltipMultiSelectItems.find((focus) => focus.el.nativeElement === el);
          focusedElement?.show();
        });
        break;
    }
  }

  /**
   * @author Ministerio de Cultura
   * @createdate 2025-07-08
   * @description Implementa restricciones para el campo numérico con la propiedad `blockPaste`.
   * Este método previene las acciones de pegar (`paste`), copiar (`copy`) y cortar (`cut`) sobre el componente,
   * obligando al usuario a digitar el valor manualmente.
   *
   * Si el evento es de tipo `paste`, se limpia el valor del campo y se establece a `null` en el formulario.
   *
   * @param event Evento del portapapeles (`ClipboardEvent`) que se desea bloquear.
   */
  preventPaste(event: ClipboardEvent): void {
    event.preventDefault();

    if (event.type === 'paste') {
      const input = event.target as HTMLInputElement;
      input.value = '';

      const controlName = input.getAttribute('formcontrolname');
      if (controlName) {
        const control = this.form.get(controlName);
        control?.setValue(null);
      }
    }
  }

  countCharacters(question: TextFieldTwo | TextareaFieldTwo): number {
    return question.maxlength - (this.form.get(question.key)?.value?.length || 0);
  }

}
